import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { RestaurantService } from './restaurant.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DomainDetectionService {
  private customDomainRestaurant: any = null;
  private mainDomains = [
    'localhost', '127.0.0.1',
    // India platform
    'ordrciti.in', 'www.ordrciti.in',
    // UK / staging legacy
    'foodciti.com', 'www.foodciti.com',
    'foodciti.arcinfosoft.com', 'www.foodciti.arcinfosoft.com',
    'yourmainapp.com'
  ];
  private enforcedRestaurantId: string | null = null;
  private enforcedDeviceMode: 'mobile' | 'desktop' = 'desktop';
  private hasStartedCustomDomainEnforcement = false;

  constructor(
    private restaurantService: RestaurantService,
    private router: Router
  ) {}

  async detectAndRouteCustomDomain(): Promise<void> {
    const currentDomain = this.normalizeDomain(window.location.hostname);
    

    // Skip if it's a main domain
    if (this.isMainDomain(currentDomain)) {
      return;
    }

    try {
      const response = await this.getRestaurantByDomainWithFallback(currentDomain);

      if (response.success && response.data.restaurant) {
        this.customDomainRestaurant = response.data.restaurant;
        const restaurantId = this.customDomainRestaurant._id;


        // Detect device type
        const isMobile = this.isMobileDevice();
        
        // Get current path
        const currentPath = window.location.pathname;

        // If already on the correct restaurant page, don't redirect
        if (currentPath.includes(`/restaurant/${restaurantId}`) || 
            currentPath.includes(`/restaurant-mobile/${restaurantId}`)) {
          return;
        }

        // Allow utility paths (feedback, etc.) to work on custom domains without redirecting
        if (currentPath.startsWith('/feedback/')) {
          this.enforcedRestaurantId = restaurantId;
          this.enforcedDeviceMode = isMobile ? 'mobile' : 'desktop';
          this.startCustomDomainRouteEnforcement();
          return;
        }

        // Route to appropriate page based on device
        if (isMobile) {
          await this.router.navigate(['/restaurant-mobile', restaurantId]);
        } else {
          await this.router.navigate(['/restaurant', restaurantId]);
        }

        this.enforcedRestaurantId = restaurantId;
        this.enforcedDeviceMode = isMobile ? 'mobile' : 'desktop';
        this.startCustomDomainRouteEnforcement();
      }
    } catch (error: any) {
      // If restaurant not found for this domain, continue with normal routing
      if (error.status === 404) {
      } else {
      }
    }
  }

  private normalizeDomain(domain: string): string {
    return String(domain || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .replace(/:\d+$/, '')
      .replace(/\.$/, '');
  }

  private buildDomainCandidates(domain: string): string[] {
    const normalized = this.normalizeDomain(domain);
    if (!normalized) return [];

    const withoutWww = normalized.replace(/^www\./, '');
    const withWww = withoutWww ? `www.${withoutWww}` : '';
    return Array.from(new Set([normalized, withoutWww, withWww].filter(Boolean)));
  }

  private async getRestaurantByDomainWithFallback(domain: string): Promise<any> {
    const candidates = this.buildDomainCandidates(domain);
    let lastError: any = null;

    for (const candidate of candidates) {
      try {
        return await firstValueFrom(this.restaurantService.getRestaurantByDomain(candidate));
      } catch (error: any) {
        lastError = error;
        if (error?.status !== 404) {
          throw error;
        }
      }
    }

    throw lastError;
  }

  private isMainDomain(domain: string): boolean {
    const normalizedDomain = this.normalizeDomain(domain);
    return this.mainDomains.some(mainDomain => {
      const normalizedMainDomain = this.normalizeDomain(mainDomain);
      return normalizedDomain === normalizedMainDomain;
    }
    );
  }

  private startCustomDomainRouteEnforcement(): void {
    if (this.hasStartedCustomDomainEnforcement) return;
    this.hasStartedCustomDomainEnforcement = true;

    this.enforceRestaurantOnlyRoute();

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.enforceRestaurantOnlyRoute();
      }
    });
  }

  private enforceRestaurantOnlyRoute(): void {
    const restaurantId = this.enforcedRestaurantId;
    if (!restaurantId) return;

    const currentPath = window.location.pathname;

    // Allow feedback and other utility paths to work on custom domains
    if (currentPath.startsWith('/feedback/')) return;

    // Allow legal pages to work on custom domains
    if (restaurantId &&
        (currentPath === `/restaurant/${restaurantId}/privacy-policy` ||
         currentPath === `/restaurant/${restaurantId}/terms`)) return;
    const targetBasePath = this.enforcedDeviceMode === 'mobile'
      ? `/restaurant-mobile/${restaurantId}`
      : `/restaurant/${restaurantId}`;

    const isOnAllowedPath =
      currentPath === targetBasePath ||
      currentPath.startsWith(`${targetBasePath}/`);

    if (!isOnAllowedPath) {
      void this.router.navigateByUrl(targetBasePath, { replaceUrl: true });
    }
  }

  private isMobileDevice(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  }

  getCustomDomainRestaurant(): any {
    return this.customDomainRestaurant;
  }

  isCustomDomain(): boolean {
    return this.customDomainRestaurant !== null;
  }
}
